import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowRight, Clock, Award, Lightbulb, FileText, Play } from 'lucide-react';
import { MathRenderer } from '../math/MathRenderer';
import QuestionMediaDisplay from './QuestionMediaDisplay';

const TestTakerView = ({ test, questions, onFinish, onSubmitTest, onBackToList }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [testState, setTestState] = useState(test.instructions ? 'instructions' : 'active');
  const [timeLeft, setTimeLeft] = useState(test.timeLimitPerQuestion || 0);
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [showHint, setShowHint] = useState(false);
  
  const currentQuestion = useMemo(() => questions[currentIndex], [questions, currentIndex]);

  const finishTest = useCallback(() => {
    setTestState('finished');
  }, []);

  useEffect(() => {
    if (testState === 'finished') {
      const correctCount = Object.values(userAnswers).filter(a => a.isCorrect).length;
      const totalMarks = Object.entries(userAnswers).reduce((total, [qId, answer]) => {
        if (answer.isCorrect) {
          const question = questions.find(q => q.id === qId);
          return total + (question?.marks || 1);
        }
        return total;
      }, 0);
      
      const resultSummary = {
        testId: test.id,
        testName: test.name,
        score: correctCount,
        totalQuestions: questions.length,
        totalMarks: totalMarks,
        maxMarks: test.totalMarks || questions.reduce((sum, q) => sum + (q.marks || 1), 0),
        date: Date.now(),
      };
      
      if (onSubmitTest) {
        onSubmitTest(resultSummary, userAnswers);
      }
      
      if (onFinish) {
        onFinish(resultSummary, userAnswers);
      }
    }
  }, [testState, userAnswers, test, questions, onFinish, onSubmitTest]);

  useEffect(() => {
    if (testState !== 'active' || test.timeLimitPerQuestion <= 0 || !currentQuestion) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitAnswer(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentIndex, testState, test.timeLimitPerQuestion, currentQuestion]);

  const handleOptionToggle = (optionId) => {
    if (testState !== 'active') return;
    setSelectedOptionIds(prev =>
      prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
    );
  };
  
  const handleSubmitAnswer = (isTimeout = false) => {
    if (!currentQuestion) return;
    
    const selectedIds = isTimeout ? [] : selectedOptionIds;
    const correctIdsSet = new Set(currentQuestion.correctOptionIds);
    const selectedIdsSet = new Set(selectedIds);
    
    let isCorrect = correctIdsSet.size === selectedIdsSet.size;
    if (isCorrect) {
      for (const id of correctIdsSet) {
        if (!selectedIdsSet.has(id)) {
          isCorrect = false;
          break;
        }
      }
    }
    
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: { 
        selectedIds, 
        isCorrect,
        isSubQuestion: false,
        parentQuestionId: null,
        marks: isCorrect ? (currentQuestion.marks || 1) : 0
      }
    }));
    
    setTestState('answered');
    setShowHint(false);
  };

  const handleNextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setTimeLeft(test.timeLimitPerQuestion || 0);
      setSelectedOptionIds([]);
      setTestState('active');
      setShowHint(false);
    } else {
      finishTest();
    }
  };

  // Results screen
  if (testState === 'finished') {
    const score = Object.values(userAnswers).filter(a => a.isCorrect).length;
    const totalMarks = Object.entries(userAnswers).reduce((total, [qId, answer]) => {
      if (answer.isCorrect) {
        const question = questions.find(q => q.id === qId);
        return total + (question?.marks || 1);
      }
      return total;
    }, 0);
    const maxMarks = questions.reduce((sum, q) => sum + (q.marks || 1), 0);
    const percentage = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
    const marksPercentage = maxMarks > 0 ? Math.round((totalMarks / maxMarks) * 100) : 0;
    
    let outcomeMessage = '';
    if (test.passingScore) {
      outcomeMessage = percentage >= test.passingScore 
        ? 'Congratulations, you passed!' 
        : 'Keep studying, you can do it!';
    }

    return (
      <div className="text-center bg-slate-800 p-8 rounded-lg shadow-xl">
        <h2 className="text-3xl font-bold text-green-400">Test Complete!</h2>
        <p className="text-slate-300 my-2">You have completed the "{test.name}" test.</p>
        
        <div className="my-6 space-y-2">
          <p className="text-5xl font-bold">
            {score} / {questions.length} 
            <span className="text-3xl text-slate-400 ml-2">({percentage}%)</span>
          </p>
          {maxMarks > questions.length && (
            <p className="text-xl text-amber-400 flex items-center justify-center gap-2">
              <Award size={20} />
              {totalMarks} / {maxMarks} marks ({marksPercentage}%)
            </p>
          )}
        </div>
        
        {outcomeMessage && (
          <p className={`text-lg font-semibold mb-6 ${percentage >= test.passingScore ? 'text-green-400' : 'text-amber-400'}`}>
            {outcomeMessage}
          </p>
        )}
        
        <button 
          onClick={onBackToList} 
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-md transition"
        >
          Back to Test Center
        </button>
      </div>
    );
  }

  // Instructions screen
  if (testState === 'instructions') {
    return (
      <div className="bg-slate-800 p-8 rounded-lg shadow-xl max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-indigo-400 mb-4">{test.name}</h2>
        
        <div className="mb-6 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
          <h3 className="text-lg font-medium text-slate-200 mb-3 flex items-center gap-2">
            <FileText size={20} />
            Instructions
          </h3>
          <div className="text-slate-300 whitespace-pre-wrap">
            {test.instructions}
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-slate-700 rounded-lg">
            <span className="text-slate-400">Questions:</span>
            <span className="ml-2 text-slate-200 font-medium">{questions.length}</span>
          </div>
          {test.timeLimitPerQuestion > 0 && (
            <div className="p-3 bg-slate-700 rounded-lg">
              <span className="text-slate-400">Time per question:</span>
              <span className="ml-2 text-slate-200 font-medium">{test.timeLimitPerQuestion}s</span>
            </div>
          )}
          {test.passingScore && (
            <div className="p-3 bg-slate-700 rounded-lg">
              <span className="text-slate-400">Passing score:</span>
              <span className="ml-2 text-slate-200 font-medium">{test.passingScore}%</span>
            </div>
          )}
          {test.totalMarks && (
            <div className="p-3 bg-slate-700 rounded-lg">
              <span className="text-slate-400">Total marks:</span>
              <span className="ml-2 text-amber-400 font-medium">{test.totalMarks}</span>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button
            onClick={onBackToList}
            className="flex-1 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-md transition"
          >
            Cancel
          </button>
          <button
            onClick={() => setTestState('active')}
            className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md transition flex items-center justify-center gap-2"
          >
            <Play size={20} />
            Start Test
          </button>
        </div>
      </div>
    );
  }
  
  if (!currentQuestion) {
    return (
      <div className="text-center bg-slate-800 p-8 rounded-lg shadow-xl">
        <h2 className="text-2xl font-bold text-slate-300">Loading Test...</h2>
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-800 p-6 sm:p-8 rounded-lg shadow-xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-slate-400">
          Question {currentIndex + 1} of {questions.length}
          {(currentQuestion.media?.type !== 'none' || currentQuestion.image || currentQuestion.stemImage) && (
            <div className="mb-4">
              {currentQuestion.media?.type !== 'none' ? (
                <QuestionMediaDisplay 
                  media={currentQuestion.media} 
                  className="max-w-lg mx-auto"
                />
              ) : (currentQuestion.image || currentQuestion.stemImage) ? (
                <img 
                  src={currentQuestion.image || currentQuestion.stemImage} 
                  alt="Question" 
                  className="max-h-48 mx-auto rounded-lg"
                />
              ) : null}
            </div>
          )}
        </div>
        {test.timeLimitPerQuestion > 0 && testState === 'active' && (
          <div className={`text-lg font-bold px-3 py-1 rounded-md flex items-center gap-2 ${
            timeLeft <= 10 ? 'text-red-400 animate-pulse bg-red-900/30' : 'text-slate-300 bg-slate-700'
          }`}>
            <Clock size={18} />
            {timeLeft}s
          </div>
        )}
      </div>
      
      {/* Question */}
      <div className="w-full max-w-2xl mx-auto">
        <div className="mb-6">
          {currentQuestion.image && (
            <img 
              src={currentQuestion.image} 
              alt="Question" 
              className="max-h-48 mx-auto mb-4 rounded-lg"
            />
          )}
          <h3 className="text-xl sm:text-2xl font-bold text-center text-slate-200">
            <MathRenderer content={currentQuestion.text} enableMarkdown={true} enableMath={true} />
          </h3>
        </div>
        
        {/* Options - No feedback shown after answer */}
        <div className="space-y-3 mb-6">
          {currentQuestion.options.map((option) => {
            const isSelected = selectedOptionIds.includes(option.id);
            
            let optionStyle = 'border-slate-600 bg-slate-700 hover:bg-slate-600';
            if (isSelected) {
              optionStyle = 'border-indigo-500 bg-indigo-900/30';
            }
            
            return (
              <label
                key={option.id}
                className={`flex items-center p-4 rounded-md border-2 transition-all duration-200 ${
                  testState === 'active' ? 'cursor-pointer' : 'cursor-default'
                } ${optionStyle}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleOptionToggle(option.id)}
                  disabled={testState !== 'active'}
                  className="h-5 w-5 rounded bg-slate-600 border-slate-500 text-indigo-500 focus:ring-indigo-600 mr-4"
                />
                <span className="text-slate-200 flex-grow">
                  <MathRenderer content={option.text} enableMarkdown={false} enableMath={true} />
                </span>
              </label>
            );
          })}
        </div>

        {/* Hint Button */}
        {testState === 'active' && currentQuestion.hint && (
          <button
            onClick={() => setShowHint(!showHint)}
            className="mb-4 text-sm text-yellow-400 hover:text-yellow-300 flex items-center gap-1"
          >
            <Lightbulb size={16} />
            {showHint ? 'Hide Hint' : 'Show Hint'}
          </button>
        )}

        {/* Hint Display */}
        {showHint && currentQuestion.hint && (
          <div className="mb-4 p-4 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
            {currentQuestion.hint.text && (
              <p className="text-yellow-300 text-sm">
                <MathRenderer content={currentQuestion.hint.text} enableMarkdown={true} enableMath={true} />
              </p>
            )}
            {currentQuestion.hint.audio?.url && (
              <audio controls className="w-full mt-2" src={currentQuestion.hint.audio.url} />
            )}
          </div>
        )}

        {/* Action Buttons */}
        {testState === 'active' ? (
          <button
            onClick={() => handleSubmitAnswer(false)}
            disabled={selectedOptionIds.length === 0}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-md transition disabled:bg-slate-600 disabled:cursor-not-allowed"
          >
            Submit Answer
          </button>
        ) : (
          /* Next Question Button - No correct/incorrect feedback */
          <button
            onClick={handleNextQuestion}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-md transition flex items-center justify-center gap-2"
          >
            {currentIndex === questions.length - 1 ? (
              'Finish Test'
            ) : (
              <>
                Next Question
                <ArrowRight size={20} />
              </>
            )}
          </button>
        )}

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Progress</span>
            <span>{Math.round(((currentIndex + (testState === 'answered' ? 1 : 0)) / questions.length) * 100)}%</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-indigo-500 transition-all duration-300"
              style={{ width: `${((currentIndex + (testState === 'answered' ? 1 : 0)) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestTakerView;